import { useState, useEffect, useRef } from "react";
import { jwtFetch } from "./utils/apiClient";
import { format, parse } from "date-fns";
import { Tabs, Tab, Modal } from "react-bootstrap";
import { config, priceFormat } from "./Components/Config";
import Menu from "./Components/Menu";
import Header from "./Components/Header";

const Lead = () => {
  const crmUser = JSON.parse(localStorage.getItem('crmUser'));
  const [leads, setLeads] = useState(null);
  const [expandedLeadEnquiryIds, setExpandedLeadEnquiryIds] = useState([]);
  const [tableFilters, setTableFilters] = useState(null);
  const [tableSearch, setTableSearch] = useState('');
  const [tableSortBy, setTableSortBy] = useState('lead_code');
  const [tableSortType, setTableSortType] = useState('descending');
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters] = useState([{ field: '', value: '' }]);
  const [showImport, setShowImport] = useState(false);
  const [importData, setImportData] = useState(null);

  const searchInputRef = useRef(null);

  // Manual/official lead template — matched by header text. Kept for
  // whatever source still exports with these English column names.
  const csvHeaders = [
    { labels: ["Contact Name", "Nama"], key: "cperson_name" },
    { labels: ["Contact Phone", "No Handphone"], key: "cperson_phone" },
    { labels: ["Contact Email", "Alamat Email"], key: "cperson_email" },
    { labels: ["Contact Position", "Posisi/ Jabatan"], key: "cperson_position" },
    { labels: ["Company Name", "Nama Perusahaan"], key: "company_name" },
    { labels: ["Product Line"], key: "lead_category" },
    { labels: ["Product Detail"], key: "lead_enquiry" },
    { labels: ["Source Leads"], key: "source_name" },
    { labels: ["Assigned to"], key: "sales_name" },
    { labels: ["Created By"], key: "marketing_name" },
    { labels: ["Date Created", "Timestamp"], key: "created_on" },
    { labels: ["Deal Type Check"], key: "deal_type_init" },
    { labels: ["Deal Type Update"], key: "deal_type" },
  ];

  // Google Forms event-survey export — matched by column POSITION, not by
  // header text. Every event's survey re-words its questions, but Google
  // Forms always puts these seven demographic questions first and in this
  // order, and always names the very first column "Timestamp" (that's a
  // Forms platform convention, not something an event organizer edits) —
  // so that first header is used purely as a format signal, never mapped
  // by name past that. `null` marks a column that isn't captured.
  const GOOGLE_FORMS_COLUMNS = [
    "created_on",       // 0: Timestamp
    "cperson_name",     // 1: Nama
    "cperson_email",    // 2: Alamat Email
    "cperson_phone",    // 3: No Handphone
    "company_name",     // 4: Nama Perusahaan
    null,               // 5: Struktur Organisasi Level (not stored)
    "cperson_position", // 6: Posisi/ Jabatan
  ];

  useEffect(() => {
    setTableFilters({
      leadStatus: 'New',
    });
    getLeads();
  }, []);

  const getLeads = async () => {
    try {
      let requestLead = {
        lead_department: 'Marketing'
      };
      const fetchLeads = await jwtFetch(config.ENDPOINT + 'get/lead', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestLead)
      });
      let responseLeads = await fetchLeads.json();
      if (responseLeads.status === 200) {
        for (let i in responseLeads.data) {
          let label = responseLeads.data[i].lead_status;
          if (label === 'Marketing Qualified Lead') {
            label = responseLeads.data[i].company_name && responseLeads.data[i].bant_budget && responseLeads.data[i].bant_authority && responseLeads.data[i].bant_need && responseLeads.data[i].bant_timeline && responseLeads.data[i].source_name && responseLeads.data[i].assigned_to ? 'Ready to Send' : 'Qualifying';
          } else if (label === 'Sent to Sales') {
            label = 'MQL';
          } else if (label === 'Qualifying') {
            label = responseLeads.data[i].opportunity_code ? 'SQL' : 'Qualifying';
          }
          responseLeads.data[i].label = label;
          if (!responseLeads.data[i].company_name) {
            if (responseLeads.data[i].website_form) {
              const websiteForm = JSON.parse(responseLeads.data[i].website_form);
              responseLeads.data[i].company_name = websiteForm.company_name;
              responseLeads.data[i].contact_name = websiteForm.contact_name;
              responseLeads.data[i].contact_email = websiteForm.contact_email;
              responseLeads.data[i].contact_phone = websiteForm.contact_phone;
            }
          }
          let searchData = String(responseLeads.data[i].lead_code ?? '').toLowerCase();
          searchData += responseLeads.data[i].company_name ? responseLeads.data[i].company_name.toLowerCase() : '';
          searchData += responseLeads.data[i].company_billing_name ? responseLeads.data[i].company_billing_name.toLowerCase() : '';
          searchData += responseLeads.data[i].contact_name ? responseLeads.data[i].contact_name.toLowerCase() : '';
          searchData += responseLeads.data[i].contact_email ? responseLeads.data[i].contact_email.toLowerCase() : '';
          searchData += responseLeads.data[i].contact_phone ? responseLeads.data[i].contact_phone.toLowerCase() : '';
          searchData += responseLeads.data[i].lead_code ? responseLeads.data[i].lead_code.toLowerCase() : '';
          searchData += responseLeads.data[i].lead_enquiry ? responseLeads.data[i].lead_enquiry.toLowerCase() : '';
          searchData += responseLeads.data[i].lead_category ? responseLeads.data[i].lead_category.toLowerCase() : '';
          searchData += responseLeads.data[i].lead_product ? responseLeads.data[i].lead_product.toLowerCase() : '';
          searchData += responseLeads.data[i].lead_status ? responseLeads.data[i].lead_status.toLowerCase() : '';
          searchData += responseLeads.data[i].opportunity_code ? (responseLeads.data[i].opportunity_code.toLowerCase() + 'sql') : '';
          searchData += responseLeads.data[i].source_name ? responseLeads.data[i].source_name.toLowerCase() : '';
          searchData += responseLeads.data[i].source_channel ? responseLeads.data[i].source_channel.toLowerCase() : '';
          searchData += responseLeads.data[i].source_status ? responseLeads.data[i].source_status.toLowerCase() : '';
          searchData += responseLeads.data[i].marketing_name ? responseLeads.data[i].marketing_name.toLowerCase() : '';
          searchData += responseLeads.data[i].sales_name ? responseLeads.data[i].sales_name.toLowerCase() : '';
          responseLeads.data[i].search = searchData;
        }
        setLeads(responseLeads.data);
        setIsLoadingPage(false);
      } else {
        alert(responseLeads.message);
      }
    } catch (error) {
      console.error('Error in getLeads:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  }

  const filterTable = (key, value) => {
    let filters = { ...tableFilters };
    if (key === 'status') {
      if (value === 'Sales Qualified Lead') {
        filters.leadStatus = ['Sales Qualified Lead', 'Qualifying', 'Sent to Sales'];
      } else {
        filters.leadStatus = value;
      }
    }
    setTableFilters(filters);
  }

  const sortTable = (sortBy) => {
    if (tableSortBy === sortBy) {
      setTableSortType(tableSortType === 'ascending' ? 'descending' : 'ascending');
    } else {
      setTableSortBy(sortBy);
      setTableSortType('ascending');
    }
  }

  const clearSearch = () => {
    setTableSearch('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }

  const getStatusCount = (status) => {
    if (!leads) return 0;

    if (status === 'Sales Qualified Lead') {
      return leads.filter(lead =>
        (lead.lead_status === 'Sales Qualified Lead' || lead.lead_status === 'Qualifying' || lead.lead_status === 'Sent to Sales') &&
        (tableSearch === '' || lead.search.includes(tableSearch.toLowerCase())) &&
        (filters.length === 0 || filters.every(filter => {
          if (!filter.field || !filter.value) return true;
          const fieldValue = lead[filter.field];
          if (fieldValue === null || fieldValue === undefined) return false;
          const fieldString = String(fieldValue).toLowerCase();
          const filterString = String(filter.value).toLowerCase();
          return fieldString.includes(filterString);
        }))
      ).length;
    }

    return leads.filter(lead =>
      lead.lead_status === status &&
      (tableSearch === '' || lead.search.includes(tableSearch.toLowerCase())) &&
      (filters.length === 0 || filters.every(filter => {
        if (!filter.field || !filter.value) return true;
        const fieldValue = lead[filter.field];
        if (fieldValue === null || fieldValue === undefined) return false;
        const fieldString = String(fieldValue).toLowerCase();
        const filterString = String(filter.value).toLowerCase();
        return fieldString.includes(filterString);
      }))
    ).length;
  }

  const changeFilter = (index, type, value) => {
    const updated = [...filters];
    updated[index][type] = value;
    setFilters(updated);
  }

  const addFilter = () => {
    const currentFilters = [...filters];
    currentFilters.push({ field: '', value: '' });
    setFilters(currentFilters);
  }

  const removeFilter = (index) => {
    const updated = [...filters];
    updated.splice(index, 1);
    setFilters(updated);
  }

  const resetFilter = () => {
    setFilters([{ field: '', value: '' }]);
  }

  const parseCSV = (text) => {
    const rows = [];
    let row = [];
    let field = '';
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      if (char === '"') {
        if (inQuotes) {
          if (text[i + 1] === '"') {
            field += '"';
            i++;
          } else {
            inQuotes = false;
          }
        } else {
          inQuotes = true;
        }
      } else if (char === ',' && !inQuotes) {
        row.push(field.trim());
        field = '';
      } else if (char === '\n' || char === '\r') {
        if (inQuotes) {
          field += '\n';
        } else {
          row.push(field.trim());
          rows.push(row.map((val) => {
            if (val.length >= 2 && val.startsWith('"') && val.endsWith('"')) {
              return val.slice(1, -1);
            }
            return val;
          }));
          row = [];
          field = '';
          if (char === '\r' && text[i + 1] === '\n') {
            i++;
          }
        }
      } else {
        field += char;
      }
    }
    if (field.length > 0 || row.length > 0) {
      row.push(field.trim());
      rows.push(row.map((val) => {
        if (val.length >= 2 && val.startsWith('"') && val.endsWith('"')) {
          return val.slice(1, -1);
        }
        return val;
      }));
    }
    return rows;
  };

  const normalizeHeader = (header = "") =>
    header
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");

  // Shared by both import formats below: parse a timestamp cell against the
  // date shapes this feature has seen in practice, or return it unchanged
  // if none match (createLead can still take a plain string).
  const parseLeadDate = (value) => {
    if (!value) return value;
    const supportedDateFormats = ['MM/dd/yyyy', 'M/d/yyyy', 'M/d/yyyy H:mm:ss', 'MM/dd/yyyy HH:mm:ss'];
    for (let k = 0; k < supportedDateFormats.length; k++) {
      const parsed = parse(value, supportedDateFormats[k], new Date());
      if (!Number.isNaN(parsed.getTime())) {
        return format(parsed, 'yyyy-MM-dd HH:mm:ss');
      }
    }
    return value;
  };

  const finalizeImportedLead = (obj) => {
    if (!obj.source_name) {
      obj.source_name = 'Event';
    }
    if (!obj.deal_type) {
      obj.deal_type = obj.deal_type_init || 'New Business';
    }
    delete obj.deal_type_init;

    if (!obj.cperson_name && !obj.cperson_email && !obj.company_name) {
      return null;
    }
    return obj;
  };

  // See GOOGLE_FORMS_COLUMNS above for why "Timestamp" is a safe signal.
  const isGoogleFormsExport = (headerRow) =>
    normalizeHeader(headerRow?.[0]) === 'timestamp';

  const mapGoogleFormsRow = (currentline, uploadedHeaders) => {
    const obj = {};
    GOOGLE_FORMS_COLUMNS.forEach((key, index) => {
      if (!key) return;
      let value = currentline[index] ? currentline[index].trim() : '';
      if (key === 'created_on') {
        value = parseLeadDate(value);
      }
      obj[key] = value;
    });

    // Everything after the fixed demographic block is a survey question that
    // varies event to event — captured as context rather than guessed at by
    // position, paired with whatever that column's own header says this time.
    const enquiryParts = [];
    for (let j = GOOGLE_FORMS_COLUMNS.length; j < uploadedHeaders.length; j++) {
      const answer = currentline[j] ? currentline[j].trim() : '';
      if (answer) {
        enquiryParts.push(`${uploadedHeaders[j]}: ${answer}`);
      }
    }
    obj.lead_enquiry = enquiryParts.join('\n');

    return obj;
  };

  const mapTemplateRow = (currentline, uploadedHeaders, headerToKeyMap) => {
    const obj = {};
    for (let j = 0; j < uploadedHeaders.length; j++) {
      const headerLabel = normalizeHeader(uploadedHeaders[j]);
      const mappedKey = headerToKeyMap[headerLabel];
      if (mappedKey) {
        let value = currentline[j] ? currentline[j].trim() : '';
        if (mappedKey === 'created_on') {
          value = parseLeadDate(value);
        }
        obj[mappedKey] = value;
      }
    }
    return obj;
  };

  const uploadCsv = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const rows = parseCSV(text);
      if (!rows || rows.length === 0) {
        setImportData([]);
        return;
      }

      const uploadedHeaders = rows[0].map(h => h.trim());
      const useGoogleFormsMapping = isGoogleFormsExport(uploadedHeaders);

      const headerToKeyMap = {};
      csvHeaders.forEach((header) => {
        header.labels.forEach((label) => {
          headerToKeyMap[normalizeHeader(label)] = header.key;
        });
      });

      const data = [];

      for (let i = 1; i < rows.length; i++) {
        const currentline = rows[i];
        if (!currentline || currentline.length === 0 || (currentline.length === 1 && currentline[0] === '')) continue;

        const obj = useGoogleFormsMapping
          ? mapGoogleFormsRow(currentline, uploadedHeaders)
          : mapTemplateRow(currentline, uploadedHeaders, headerToKeyMap);

        const lead = finalizeImportedLead(obj);
        if (lead) {
          data.push(lead);
        }
      }
      setImportData(data);
    };
    reader.readAsText(file);
  };

  const importLead = async () => {
    try {
      setIsLoadingPage(true);
      if (!importData) {
        alert('Updated CSV file is required');
        setIsLoadingPage(false);
        return;
      }
      const requestImport = {
        leads: importData,
        created_by: crmUser?.user_keyid || null,
      };
      const fetchImport = await jwtFetch(config.ENDPOINT + 'import/lead', {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestImport)
      });
      const responseImport = await fetchImport.json();
      if (responseImport.status === 200) {
        window.location.reload();
      } else {
        setIsLoadingPage(false);
        alert(responseImport.message);
      }
    } catch (error) {
      console.error('Error in importLead:', error);
      alert('An error occurred: ' + (error.message || error));
      setIsLoadingPage(false);
    }
  }

  return (
    <div id="lead-list">
      <div className="container-fluid p-0">
        <div className="row g-0 mh-100vh">
          <div className="col-md-2 border-end">
            <div className="d-md-flex d-none align-items-center justify-content-center p-1 h-48">
              <a className="h-100" href="/dashboard"><img className="h-100" src="/cuancuan.png" alt="PointStar" /></a>
            </div>
            <hr className="m-0" />
            <Menu active="lead" />
          </div>
          <div className="col-md-10">
            <Header />
            <hr className="m-0" />
            <div className="main">
              <div className="p-4">

                <section className="mb-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-center justify-content-between gap-4">
                      <div className="fs-24">Lead List</div>
                    </div>
                    <div className="d-flex align-items-center justify-content-between gap-2">
                      <div className="input-group input-search">
                        <button className="btn bg-white" type="button" onClick={() => clearSearch()}>{tableSearch ? <i className="bi bi-x-lg" /> : <i className="bi bi-search" />}</button>
                        <input className="form-control" type="text" placeholder="Search" value={tableSearch} onChange={(e) => setTableSearch(e.target.value.trim())} ref={searchInputRef} autoFocus />
                      </div>
                      <div className="d-flex align-items-center justify-content-between gap-2">
                        <button className="btn btn-outline-secondary" type="button" onClick={() => setShowFilter(true)}><i className="bi bi-filter me-1" /> Filter {filters.filter(f => f.field && f.value).length > 0 ? `(${filters.filter(f => f.field && f.value).length})` : ''}</button>
                        {crmUser.user_level !== 'View' && (
                          <>
                            <button className="btn btn-link bg-gold" type="button" onClick={() => window.location.href = '/lead/add'} target="_blank" rel="noreferrer"><i className="bi bi-plus-circle me-1" /> Add</button>
                            <button className="btn btn-link bg-gold" type="button" onClick={() => setShowImport(true)}><i className="bi bi-upload me-1" /> Import</button>
                          </>
                        )}
                        {/* <button className="btn btn-link bg-gold" type="button" onClick={() => window.location.href = '/lead/report'} target="_blank" rel="noreferrer"><i className="bi bi-bar-chart-line me-1"/> Report</button> */}

                      </div>
                    </div>
                  </div>
                  <div className="bg-white border-radius-4 p-4">
                    <Tabs className="mb-3" defaultActiveKey="New" onSelect={(key) => filterTable('status', key)}>
                      <Tab eventKey="New" title={`New${getStatusCount('New') > 0 ? ` (${getStatusCount('New')})` : ''}`} />
                      <Tab eventKey="Marketing Qualified Lead" title={`MQL${getStatusCount('Marketing Qualified Lead') > 0 ? ` (${getStatusCount('Marketing Qualified Lead')})` : ''}`} />
                      <Tab eventKey="Sales Qualified Lead" title={`SQL${getStatusCount('Sales Qualified Lead') > 0 ? ` (${getStatusCount('Sales Qualified Lead')})` : ''}`} />
                      <Tab eventKey="Nurture" title={`Nurture${getStatusCount('Nurture') > 0 ? ` (${getStatusCount('Nurture')})` : ''}`} />
                      <Tab eventKey="Disqualified" title={`DQ${getStatusCount('Disqualified') > 0 ? ` (${getStatusCount('Disqualified')})` : ''}`} />
                    </Tabs>
                    {leads && leads.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-hover">
                          <thead>
                            <tr>
                              <th className="bg-gold w-40">#</th>
                              <th className="bg-gold pointer text-start" onClick={() => sortTable('lead_code')}>Number {tableSortBy === 'lead_code' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                              <th className="bg-gold pointer text-start" onClick={() => sortTable('label')}>Label {tableSortBy === 'label' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                              <th className="bg-gold pointer text-start w-300" onClick={() => sortTable('company_name')}>Company Name {tableSortBy === 'company_name' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                              <th className="bg-gold pointer text-start" onClick={() => sortTable('lead_category')}>Product{tableSortBy === 'lead_category' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                              <th className="bg-gold pointer text-start" onClick={() => sortTable('bant_budget')}>Budget{tableSortBy === 'bant_budget' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                              <th className="bg-gold pointer text-start w-300" onClick={() => sortTable('lead_enquiry')}>Enquiry{tableSortBy === 'lead_enquiry' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                              <th className="bg-gold pointer text-start" onClick={() => sortTable('source_name')}>Source{tableSortBy === 'source_name' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                              <th className="bg-gold pointer text-start" onClick={() => sortTable('sales_name')}>Sales Name{tableSortBy === 'sales_name' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                              <th className="bg-gold pointer text-start" onClick={() => sortTable('opportunity_code')}>Opportunity{tableSortBy === 'opportunity_code' && (tableSortType === 'ascending' ? <span className="bi bi-sort-down-alt ms-2" /> : <span className="bi bi-sort-up-alt ms-2" />)}</th>
                              <th className="bg-gold w-40"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...leads]
                              .sort((a, b) => {
                                const aValue = a[tableSortBy];
                                const bValue = b[tableSortBy];
                                const aNum = Number(aValue);
                                const bNum = Number(bValue);
                                const isNumeric = !isNaN(aNum) && !isNaN(bNum);
                                if (isNumeric) {
                                  return tableSortType === 'ascending' ? aNum - bNum : bNum - aNum;
                                }
                                const aStr = aValue === null || aValue === undefined ? '' : aValue.toString();
                                const bStr = bValue === null || bValue === undefined ? '' : bValue.toString();
                                return tableSortType === 'ascending'
                                  ? aStr.localeCompare(bStr)
                                  : bStr.localeCompare(aStr);
                              })
                              .filter(lead => lead.search.includes(tableSearch.toLowerCase()))
                              .filter(lead => {
                                if (tableFilters && tableFilters.leadStatus) {
                                  return Array.isArray(tableFilters.leadStatus) ? tableFilters.leadStatus.includes(lead.lead_status) : lead.lead_status === tableFilters.leadStatus;
                                }
                                return true;
                              })
                              .filter(lead => {
                                if (filters && filters.length > 0) {
                                  return filters.every(filter => {
                                    if (!filter.field || !filter.value) return true;
                                    const fieldValue = lead[filter.field];
                                    if (fieldValue === null || fieldValue === undefined) return false;
                                    const fieldString = String(fieldValue).toLowerCase();
                                    const filterString = String(filter.value).toLowerCase();
                                    return fieldString.includes(filterString);
                                  });
                                }
                                return true;
                              })
                              .map((lead, q) => (
                                <tr key={"lead-" + q}>
                                  <td valign="middle" align="center">{q + 1}</td>
                                  <td valign="middle" align="center" className="text-nowrap">{lead.lead_code}</td>
                                  <td valign="middle" align="center" className="text-nowrap">
                                    {lead.label === 'New' && (<span className="badge text-bg-secondary">New</span>)}
                                    {lead.label === 'Qualifying' && (<span className="badge text-bg-warning">Qualifying</span>)}
                                    {lead.label === 'MQL' && (<span className="badge text-bg-secondary">MQL</span>)}
                                    {lead.label === 'Ready to Send' && (<span className="badge text-bg-success">Ready to Send</span>)}
                                    {lead.label === 'Sent to Sales' && (<span className="badge text-bg-info">Sent to Sales</span>)}
                                    {lead.label === 'SQL' && (<span className="badge text-bg-success">SQL</span>)}
                                    {lead.label === 'Nurture' && (<span className="badge text-bg-warning">Nurture</span>)}
                                    {lead.label === 'Disqualified' && (<span className="badge text-bg-danger">Disqualified</span>)}
                                  </td>
                                  <td valign="middle" align="left"><strong>{lead.company_name}</strong><br />{lead.subcompany_name}</td>
                                  <td valign="middle" align="left" className="text-nowrap"><strong>{lead.lead_category}</strong><br />{lead.lead_product}</td>
                                  <td valign="middle" align="right" className="text-nowrap">{lead.bant_budget ? priceFormat(lead.bant_budget, 'IDR') : '0 IDR'}</td>
                                  <td valign="middle" align="left">
                                    <div
                                      className={expandedLeadEnquiryIds.includes(lead.id_lead) ? 'pointer' : 'text-truncate-2-lines pointer'}
                                      onClick={() => {
                                        if (expandedLeadEnquiryIds.includes(lead.id_lead)) {
                                          setExpandedLeadEnquiryIds(expandedLeadEnquiryIds.filter(id => id !== lead.id_lead));
                                        } else {
                                          setExpandedLeadEnquiryIds([...expandedLeadEnquiryIds, lead.id_lead]);
                                        }
                                      }}
                                      title="Click to expand"
                                    >{lead.lead_enquiry}</div>
                                  </td>
                                  <td valign="middle" align="left" className="text-nowrap"><strong>{lead.source_name}</strong><br />{lead.source_channel}</td>
                                  <td valign="middle" align="left" className="text-nowrap">{lead.sales_name && (<strong>{lead.sales_name}<br /></strong>)}Created on {format(new Date(lead.created_on), 'd MMM yyyy')}</td>
                                  <td valign="middle" align="center" className="text-nowrap">{lead.opportunity_code}</td>
                                  <td valign="middle" align="right"><a className="btn btn-sm bg-gold" href={'/lead/detail/' + lead.id_lead}><i className="bi bi-eye" /></a></td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p>{isLoadingPage ? 'Loading...' : 'No data'}</p>
                    )}
                  </div>
                </section>

                <Modal show={showFilter} onHide={() => setShowFilter(false)}>
                  <Modal.Header closeButton>
                    <Modal.Title>Filter Table</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    {filters.map((filter, f) => {
                      return (
                        <div className="row align-items-baseline form-group mb-2 g-2" key={"filter-" + f}>
                          <div className="col-md-4">
                            <select className="form-select" value={filter.field} onChange={(e) => changeFilter(f, 'field', e.target.value)} required>
                              <option value=""></option>
                              <option value="lead_code">Number</option>
                              <option value="company_name">Company Name</option>
                              <option value="lead_category">Product</option>
                              <option value="source_name">Source</option>
                              <option value="sales_name">Sales Name</option>
                            </select>
                          </div>
                          <div className="col-md-7">
                            <input className="form-control" type="text" value={filter.value} onChange={(e) => changeFilter(f, 'value', e.target.value)} required />
                          </div>
                          <div className="col-md-1">
                            <button className="btn btn-sm btn-link" onClick={() => removeFilter(f)}><i className="bi bi-dash-lg"></i></button>
                          </div>
                        </div>
                      );
                    })}
                    <div className="d-flex justify-content-between">
                      <button className="btn btn-outline-secondary" type="button" onClick={() => addFilter()} disabled={isLoadingPage}>Add Filter</button>
                      <button className="btn btn-link" type="button" onClick={() => resetFilter()} disabled={isLoadingPage}>Reset</button>
                    </div>
                  </Modal.Body>
                </Modal>

                <Modal show={showImport} onHide={() => setShowImport(false)}>
                  <Modal.Header closeButton>
                    <Modal.Title>Lead Import</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    <div className="row align-items-baseline form-group mb-2">
                      <div className="col-md-4">
                        <label>Upload CSV<span className="text-danger">*</span></label>
                      </div>
                      <div className="col-md-8">
                        <input className="form-control" type="file" placeholder="Upload file" accept=".csv" onChange={(e) => uploadCsv(e.target.files[0])} required />
                      </div>
                    </div>
                    <div className="d-flex justify-content-end">
                      <button className="btn btn-link" type="button" onClick={() => setShowImport(false)} disabled={isLoadingPage}>Cancel</button>
                      <button className="btn btn-link bg-gold" type="button" onClick={() => importLead()} disabled={isLoadingPage}>Import</button>
                    </div>
                  </Modal.Body>
                </Modal>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Lead;

